import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  getDashboardErrorMessage,
  useArchiveDashboardTemplateMutation,
  useDeleteDashboardTemplateMutation,
  usePublishDashboardDraftMutation,
  useRestoreDashboardTemplateMutation
} from './dashboardApi';
import { DASHBOARD_STATUS } from './dashboardContract';
import {
  getDashboardPublicationReadiness,
  validateDashboardDeletionReason,
  validateDashboardPublicationNote
} from './dashboardTemplateModel';

const NOOP = () => {};

const formatPublishedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const ReadinessSummary = ({ draftVersion, readiness }) => (
  <Alert severity={readiness.isReady ? 'success' : 'warning'} sx={{ mt: 2 }}>
    <AlertTitle>
      {readiness.isReady
        ? `Draft version ${draftVersion.versionNumber} is ready to publish`
        : `Draft version ${draftVersion.versionNumber} is not ready`}
    </AlertTitle>
    <Typography component="div" variant="body2">
      {readiness.activeMetricCount} active{' '}
      {readiness.activeMetricCount === 1 ? 'metric' : 'metrics'} and{' '}
      {readiness.visualizationCount}{' '}
      {readiness.visualizationCount === 1 ? 'graph' : 'graphs'}.
    </Typography>
    {readiness.issues.map((issue) => (
      <Typography component="div" key={issue} sx={{ mt: 0.5 }} variant="body2">
        • {issue}
      </Typography>
    ))}
  </Alert>
);

const PublishedVersionSummary = ({ publishedVersion }) => {
  if (!publishedVersion) return null;
  const publishedAt = formatPublishedAt(publishedVersion.publishedAt);

  return (
    <Box
      sx={{
        bgcolor: 'action.hover',
        borderRadius: 1,
        mt: 2,
        p: 2
      }}
    >
      <Typography fontWeight={700} variant="body2">
        Published version {publishedVersion.versionNumber}
      </Typography>
      {publishedAt && (
        <Typography color="text.secondary" variant="caption">
          Published {publishedAt}
        </Typography>
      )}
      {publishedVersion.publicationNote && (
        <Typography sx={{ mt: 1 }} variant="body2">
          {publishedVersion.publicationNote}
        </Typography>
      )}
    </Box>
  );
};

const DashboardLifecycleControls = ({
  canManage,
  customerTemplate,
  hasAssignments,
  onDeleted = NOOP,
  onSuccess = NOOP
}) => {
  const [publishOpen, setPublishOpen] = useState(false);
  const [publicationNote, setPublicationNote] = useState('');
  const [publicationError, setPublicationError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionError, setDeletionError] = useState(null);
  const [publishDraft, publishState] = usePublishDashboardDraftMutation();
  const [archiveTemplate, archiveState] = useArchiveDashboardTemplateMutation();
  const [restoreTemplate, restoreState] = useRestoreDashboardTemplateMutation();
  const [deleteTemplate, deleteState] = useDeleteDashboardTemplateMutation();
  const draftVersion = customerTemplate?.draftVersion ?? null;
  const publishedVersion = customerTemplate?.publishedVersion ?? null;
  const isArchived =
    customerTemplate?.dashboardStatus === DASHBOARD_STATUS.ARCHIVED;
  const readiness = getDashboardPublicationReadiness(draftVersion);
  const isBusy =
    publishState.isLoading ||
    archiveState.isLoading ||
    restoreState.isLoading ||
    deleteState.isLoading;

  const openPublish = () => {
    publishState.reset?.();
    setPublicationNote('');
    setPublicationError(null);
    setPublishOpen(true);
  };

  const submitPublish = async (event) => {
    event.preventDefault();
    const validation = validateDashboardPublicationNote(publicationNote);
    setPublicationError(validation.error);
    if (!validation.isValid || !readiness.isReady) return;

    try {
      await publishDraft({
        dashboardTemplateId: customerTemplate.dashboardTemplateId,
        publicationNote: validation.publicationNote
      }).unwrap();
      setPublishOpen(false);
      onSuccess(
        hasAssignments
          ? `Dashboard version ${draftVersion.versionNumber} published. Assigned locations now use this version.`
          : `Dashboard version ${draftVersion.versionNumber} published. Assign entities or industry types to make it live for locations.`
      );
    } catch (_error) {
      // RTK Query exposes the normalized error through publishState below.
    }
  };

  const openLifecycleConfirmation = (action) => {
    if (action === 'archive') archiveState.reset?.();
    if (action === 'restore') restoreState.reset?.();
    setConfirmAction(action);
  };

  const submitLifecycleAction = async () => {
    try {
      if (confirmAction === 'archive') {
        await archiveTemplate(customerTemplate.dashboardTemplateId).unwrap();
        setConfirmAction(null);
        onSuccess(
          'Customer dashboard archived. Its assigned locations now resolve another matching dashboard or the system default.'
        );
      } else if (confirmAction === 'restore') {
        await restoreTemplate(customerTemplate.dashboardTemplateId).unwrap();
        setConfirmAction(null);
        onSuccess(
          'Customer dashboard restored as an editable draft. Its assignments remain saved but do not become live until it is published.'
        );
      }
    } catch (_error) {
      // RTK Query exposes the normalized error through the active mutation.
    }
  };

  const openDelete = () => {
    deleteState.reset?.();
    setDeletionReason('');
    setDeletionError(null);
    setDeleteOpen(true);
  };

  const submitDelete = async (event) => {
    event.preventDefault();
    const validation = validateDashboardDeletionReason(deletionReason);
    setDeletionError(validation.error);
    if (!validation.isValid) return;

    try {
      const dashboardTemplateId = customerTemplate.dashboardTemplateId;
      await deleteTemplate({
        dashboardTemplateId,
        reason: validation.reason
      }).unwrap();
      setDeleteOpen(false);
      onDeleted(dashboardTemplateId);
      onSuccess(
        'Customer dashboard deleted. Its configuration, version history, and historical data remain available to administrators for restoration.'
      );
    } catch (_error) {
      // RTK Query exposes the normalized error through deleteState below.
    }
  };

  const lifecycleState =
    confirmAction === 'restore' ? restoreState : archiveState;
  const lifecycleTitle =
    confirmAction === 'restore' ? 'Restore dashboard?' : 'Archive dashboard?';
  const lifecycleButton =
    confirmAction === 'restore' ? 'Restore dashboard' : 'Archive dashboard';

  return (
    <Paper
      data-testid="dashboard-lifecycle-controls"
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', mt: 4, p: 3 }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography component="h2" fontSize={22} fontWeight={700}>
            Publication &amp; lifecycle
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Publish a version before assigning this dashboard to entities or
            industry types.
          </Typography>
        </Box>
        <Chip
          color={
            isArchived
              ? 'default'
              : customerTemplate?.dashboardStatus ===
                    DASHBOARD_STATUS.PUBLISHED && hasAssignments
                ? 'success'
                : 'info'
          }
          label={
            isArchived
              ? 'Archived'
              : customerTemplate?.dashboardStatus === DASHBOARD_STATUS.PUBLISHED
                ? hasAssignments
                  ? 'Live at assigned locations'
                  : 'Published · unassigned'
                : 'Not published'
          }
          size="small"
          variant="outlined"
        />
      </Stack>

      <PublishedVersionSummary publishedVersion={publishedVersion} />

      {draftVersion ? (
        <ReadinessSummary draftVersion={draftVersion} readiness={readiness} />
      ) : (
        !isArchived && (
          <Alert severity="info" sx={{ mt: 2 }}>
            There are no unpublished dashboard changes. Start a new draft to
            prepare the next version.
          </Alert>
        )
      )}

      {isArchived && (
        <Alert severity="info" sx={{ mt: 2 }}>
          This dashboard is retained but is not shown to customers. Restoring it
          makes its configuration editable again. Saved assignments are
          retained, but remain inactive until a customer administrator publishes
          the restored draft.
        </Alert>
      )}

      {!canManage && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Only a customer administrator can publish, archive, delete, or restore
          a dashboard.
        </Alert>
      )}

      {canManage && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="flex-end"
            spacing={1.5}
          >
            {isArchived ? (
              <>
                <Button
                  color="error"
                  disabled={isBusy}
                  onClick={openDelete}
                  variant="outlined"
                >
                  Delete dashboard
                </Button>
                <Button
                  disabled={isBusy}
                  onClick={() => openLifecycleConfirmation('restore')}
                  variant="contained"
                >
                  Restore dashboard
                </Button>
              </>
            ) : (
              <>
                <Button
                  color="error"
                  disabled={isBusy}
                  onClick={openDelete}
                  variant="text"
                >
                  Delete dashboard
                </Button>
                <Button
                  color="error"
                  disabled={isBusy}
                  onClick={() => openLifecycleConfirmation('archive')}
                  variant="outlined"
                >
                  Archive dashboard
                </Button>
                {draftVersion && (
                  <Button
                    disabled={isBusy || !readiness.isReady}
                    onClick={openPublish}
                    variant="contained"
                  >
                    Publish dashboard
                  </Button>
                )}
              </>
            )}
          </Stack>
        </>
      )}

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => !publishState.isLoading && setPublishOpen(false)}
        open={publishOpen}
      >
        <Box component="form" noValidate onSubmit={submitPublish}>
          <DialogTitle>Publish dashboard?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Version {draftVersion?.versionNumber} will become live for
              locations already assigned to this dashboard. If it is unassigned,
              publishing makes it available for assignment. The current
              published version remains in history.
            </DialogContentText>
            <TextField
              autoFocus
              error={Boolean(publicationError)}
              fullWidth
              helperText={
                publicationError ||
                `${publicationNote.length}/1000 characters. This note is retained with the published version.`
              }
              inputProps={{ maxLength: 1001 }}
              label="Publication note"
              minRows={4}
              multiline
              onChange={(event) => {
                setPublicationNote(event.target.value);
                if (publicationError) setPublicationError(null);
              }}
              required
              sx={{ mt: 2 }}
              value={publicationNote}
            />
            {publishState.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {getDashboardErrorMessage(publishState.error)}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={publishState.isLoading}
              onClick={() => setPublishOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={publishState.isLoading}
              type="submit"
              variant="contained"
            >
              {publishState.isLoading ? 'Publishing…' : 'Publish dashboard'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => !isBusy && setConfirmAction(null)}
        open={Boolean(confirmAction)}
      >
        <DialogTitle>{lifecycleTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'restore'
              ? 'The saved dashboard configuration will become an editable draft. If no draft exists, one will be created from the last published version. Saved assignments remain inactive until this dashboard is published again.'
              : `${
                  hasAssignments
                    ? 'Assigned locations will stop using this dashboard immediately and resolve another matching dashboard or the system default.'
                    : 'This unassigned dashboard will be moved out of active use.'
                } Its configuration, version history, and historical dashboard data will be retained.`}
          </DialogContentText>
          {lifecycleState.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {getDashboardErrorMessage(lifecycleState.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={isBusy} onClick={() => setConfirmAction(null)}>
            Cancel
          </Button>
          <Button
            color={confirmAction === 'archive' ? 'error' : 'primary'}
            disabled={isBusy}
            onClick={submitLifecycleAction}
            variant="contained"
          >
            {isBusy
              ? confirmAction === 'restore'
                ? 'Restoring…'
                : 'Archiving…'
              : lifecycleButton}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => !deleteState.isLoading && setDeleteOpen(false)}
        open={deleteOpen}
      >
        <Box component="form" noValidate onSubmit={submitDelete}>
          <DialogTitle>Delete dashboard?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This removes {customerTemplate?.name || 'this dashboard'} from
              active dashboard management. Its configuration, version history,
              and historical data are retained and a customer administrator can
              restore it later.
            </DialogContentText>
            {hasAssignments && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Its assignments will be removed immediately. Affected locations
                will resolve another matching dashboard or the system default.
              </Alert>
            )}
            <TextField
              autoFocus
              error={Boolean(deletionError)}
              fullWidth
              helperText={
                deletionError ||
                `${deletionReason.length}/500 characters. This reason is retained in the deletion history.`
              }
              inputProps={{ maxLength: 501 }}
              label="Deletion reason"
              minRows={3}
              multiline
              onChange={(event) => {
                setDeletionReason(event.target.value);
                if (deletionError) setDeletionError(null);
              }}
              required
              sx={{ mt: 2 }}
              value={deletionReason}
            />
            {deleteState.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {getDashboardErrorMessage(deleteState.error)}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={deleteState.isLoading}
              onClick={() => setDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="error"
              disabled={deleteState.isLoading}
              type="submit"
              variant="contained"
            >
              {deleteState.isLoading ? 'Deleting…' : 'Delete dashboard'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Paper>
  );
};

export default DashboardLifecycleControls;
